import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import type { IConversation } from '../../../extViewmodels/TextMessagingViewModel';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

const REFETCH_INTERVAL_MS = 10_000;

export type UseTextConversationArgs = {
	/** The rep's texting number (`ITelephonyConfiguration.id`). */
	phoneNumberId: string | undefined;
	/** The contact's phone in E.164. */
	toE164: string | undefined;
	enabled?: boolean;
};

/**
 * Looks up the 1:1 conversation between the rep's number and a contact phone.
 * Resolves to `null` when none exists yet; creating one is a separate mutation
 * (`useCreateTextConversation`) so a transient lookup failure never creates a
 * conversation. Polls while enabled so the opt-out flag tracks the server.
 */
export function useTextConversation({
	phoneNumberId,
	toE164,
	enabled = true,
}: UseTextConversationArgs): UseQueryResult<IConversation | null, Error> {
	const userSession = useUserSession();

	return useQuery<IConversation | null, Error>({
		queryKey: queryKeys.focusedView.textConversation(phoneNumberId, toE164),
		enabled: enabled && !!phoneNumberId && !!toE164,
		refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IConversation>(
				ImpersonationBroker.composeApiUrl({ urlPath: `conversation/phoneNumber/${phoneNumberId}/conversation` }),
				'POST',
				{ contactPhoneNumbers: [toE164] }
			);
			if (result.success) {
				return result.value ?? null;
			}
			if (result.systemCode === 404) {
				return null;
			}
			throw asApiError(result);
		},
		meta: { errorLogging: { category: 'FocusedViewTexting', action: 'TextConversationQuery' } },
	});
}
